import { cn } from '@/shared/lib/utils';
import { formatWon } from '@/shared/utils/format';
import { useLayoutEffect, useRef, useState } from 'react';

/** 한 달 값 — 응답에 없는 달은 넘기지 않는다 */
export interface ChartMonth {
  /** 1 ~ 12 */
  month: number;
  /** 처방(예상) — 점 · 점선. 미등록이면 0 */
  estimated: number;
  /** 정산(확정) — 막대. 정산이 끝난 달만 값이 있다 */
  confirmed: number | null;
  /** 처방 미등록 — 점 없이 0 위치, 툴팁 "미등록" */
  unregistered?: boolean;
}

/*
 * Figma 실적_그래프(514:432) 치수 — 차트 높이 224
 * 가로선 5줄(0 · 41 · 82 · 123 · 164), 막대 바닥 164, 월 라벨 183 ~ 205
 * 막대 24 · 둥근 끝, 점 10, 점선 2px(8 · 8), 선택 · 호버 열 배경 높이 165
 */
const BASELINE = 164;
const PLOT_HEIGHT = 140;
const GRID = [0, 41, 82, 123, 164];
const SIDE_PADDING = 24;

/** 정산(확정) 막대 기본색 — Figma 실적_그래프 State=기본 Rectangle (#c5e9cd, 팔레트에 없는 값) */
const BAR_LIGHT = 'bg-[#c5e9cd]';

/**
 * 실적 그래프 (M-08 실적 금액 추이 · M-09 병의원 월별 추세)
 * - 12개월 고정. lastMonth 이후는 미도래(회색 라벨, 선택 · 호버 없음)
 * - 막대: 정산(확정), 점선 + 점: 처방(예상). 선택 · 호버한 달은 진한 색
 * - 호버하면 회색 열 배경 + 툴팁(실적_그래프호버 587:793): 월 · 처방 · 정산, 미등록이면 "미등록"
 * - onSelect가 있으면 달을 눌러 고른다 (M-08 선택 월 패널)
 */
export const PerformanceChart = ({
  months,
  lastMonth,
  selectedMonth,
  onSelect,
  className,
}: {
  months: ChartMonth[];
  /** 조회 가능한 마지막 달(현재 정산월). 0이면 모두 미도래 */
  lastMonth: number;
  selectedMonth?: number;
  onSelect?: (month: number) => void;
  className?: string;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [hovered, setHovered] = useState<number | null>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colWidth = width > 0 ? (width - SIDE_PADDING * 2) / 12 : 0;
  const centerX = (m: number) => SIDE_PADDING + colWidth * (m - 0.5);
  const byMonth = new Map(months.map((m) => [m.month, m]));
  const max = Math.max(
    1,
    ...months.map((m) => Math.max(m.estimated, m.confirmed ?? 0)),
  );
  const yOf = (v: number) => BASELINE - (v / max) * PLOT_HEIGHT;
  const linePoints = [...months]
    .sort((a, b) => a.month - b.month)
    .map((m) => `${centerX(m.month)},${yOf(m.estimated)}`)
    .join(' ');

  const tooltipMonth = hovered === null ? undefined : byMonth.get(hovered);
  const highlighted = (m: number) => m === selectedMonth || m === hovered;

  return (
    <div
      ref={ref}
      className={cn('relative h-[224px] w-full', className)}
      onMouseLeave={() => setHovered(null)}
    >
      {/* 선택 · 호버 열 배경 */}
      {colWidth > 0 &&
        [selectedMonth, hovered].map(
          (m, i) =>
            m !== undefined &&
            m !== null &&
            (i === 0 || m !== selectedMonth) && (
              <div
                key={i}
                aria-hidden
                className={cn(
                  'absolute top-0 h-[165px]',
                  i === 0 ? 'bg-brand-100' : 'bg-surface-grey',
                )}
                style={{
                  left: SIDE_PADDING + colWidth * (m - 1),
                  width: colWidth,
                }}
              />
            ),
        )}

      {/* 가로선 */}
      {GRID.map((y) => (
        <div
          key={y}
          aria-hidden
          className="absolute inset-x-0 h-px bg-grey-100"
          style={{ top: y }}
        />
      ))}

      {colWidth > 0 && (
        <>
          {/* 정산(확정) 막대 */}
          {months.map(
            (m) =>
              m.confirmed !== null &&
              m.confirmed > 0 && (
                <div
                  key={`bar-${m.month}`}
                  aria-hidden
                  className={cn(
                    'absolute w-6 rounded-full transition-colors',
                    highlighted(m.month) ? 'bg-green-500' : BAR_LIGHT,
                  )}
                  style={{
                    left: centerX(m.month) - 12,
                    top: Math.min(yOf(m.confirmed), BASELINE - 8),
                    bottom: 224 - BASELINE,
                  }}
                />
              ),
          )}

          {/* 처방(예상) 점선 */}
          {months.length > 1 && (
            <svg
              aria-hidden
              className="pointer-events-none absolute inset-0"
              width={width}
              height={224}
            >
              <polyline
                points={linePoints}
                fill="none"
                className="stroke-brand-500"
                strokeWidth={2}
                strokeDasharray="8 8"
              />
            </svg>
          )}

          {/* 처방(예상) 점 — 미등록 달은 그리지 않는다 */}
          {months.map(
            (m) =>
              !m.unregistered && (
                <div
                  key={`dot-${m.month}`}
                  aria-hidden
                  className={cn(
                    'absolute size-2.5 rounded-full',
                    highlighted(m.month) ? 'bg-brand-500' : 'bg-brand-300',
                  )}
                  style={{
                    left: centerX(m.month) - 5,
                    top: yOf(m.estimated) - 5,
                  }}
                />
              ),
          )}
        </>
      )}

      {/* 월 열 — 호버 · 선택 영역과 x축 라벨 */}
      <div className="absolute inset-x-0 top-0 flex h-[205px] px-6">
        {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => {
          const arrived = m <= lastMonth;
          const selected = m === selectedMonth;
          const label = (
            <span
              className={cn(
                'absolute inset-x-0 top-[183px] flex justify-center',
                !arrived && 'text-fg-light',
              )}
            >
              <span
                className={cn(
                  'rounded-8 px-2',
                  selected
                    ? 'bg-brand-500 text-body-14-m text-fg-white'
                    : 'text-body-14-r',
                )}
              >
                {m}월
              </span>
            </span>
          );
          return arrived ? (
            <button
              key={m}
              type="button"
              disabled={!onSelect}
              aria-pressed={onSelect ? selected : undefined}
              aria-label={`${m}월`}
              onMouseEnter={() => setHovered(m)}
              onFocus={() => setHovered(m)}
              onBlur={() => setHovered(null)}
              onClick={() => onSelect?.(m)}
              className="relative flex-1 text-fg-primary outline-none enabled:cursor-pointer disabled:cursor-default"
            >
              {label}
            </button>
          ) : (
            <div key={m} className="relative flex-1">
              {label}
            </div>
          );
        })}
      </div>

      {/* 호버 툴팁 */}
      {hovered !== null && colWidth > 0 && (
        <div
          role="tooltip"
          className="pointer-events-none absolute z-10 flex w-36 flex-col rounded-12 bg-grey-900 px-3 py-2 shadow-500"
          style={{
            top: 127,
            ...(hovered <= 8
              ? { left: centerX(hovered) + 28 }
              : { right: width - centerX(hovered) + 28 }),
          }}
        >
          <p className="text-body-14-sb text-fg-white">{hovered}월</p>
          <TooltipRow
            label="처방"
            value={
              !tooltipMonth || tooltipMonth.unregistered
                ? '미등록'
                : formatWon(tooltipMonth.estimated)
            }
          />
          <TooltipRow
            label="정산"
            value={
              tooltipMonth?.confirmed ? formatWon(tooltipMonth.confirmed) : '-'
            }
          />
        </div>
      )}
    </div>
  );
};

const TooltipRow = ({ label, value }: { label: string; value: string }) => (
  <p className="flex items-center justify-between gap-4 text-body-14-r">
    <span className="text-fg-white-secondary">{label}</span>
    <span className="text-fg-white">{value}</span>
  </p>
);

/** 범례 — 처방(예상) 점선 + 점, 정산(확정) 초록 점 */
export const ChartLegend = () => (
  <div className="flex items-center gap-4 text-body-13-r text-fg-primary">
    <span className="flex items-center gap-1">
      <svg width="30" height="10" aria-hidden className="shrink-0">
        <line
          x1="0"
          y1="5"
          x2="30"
          y2="5"
          className="stroke-brand-500"
          strokeWidth="2"
          strokeDasharray="8 8"
        />
        <circle cx="15" cy="5" r="5" className="fill-brand-500" />
      </svg>
      처방(예상)
    </span>
    <span className="flex items-center gap-1">
      <span aria-hidden className="size-2.5 rounded-full bg-green-500" />
      정산(확정)
    </span>
  </div>
);
